import * as React from 'react'
import { ReactElement, useEffect, useMemo, useRef, useState } from 'react'
import { IActionButton } from '../../../components/ActionButtons'
import { useDispatch, useSelector } from 'react-redux'
import { Store } from '../../../reducers'
import { DataPromise, shouldDispatchLoad } from '../../../context/AxiosContext'
import { InvoiceAction, InvoiceOverviewDto, InvoiceStatus } from '../../../config/finance/invoiceConfig'
import ActionHeader from '../../../components/Render/ActionHeader'
import InvoiceList, { InvoiceListSettings } from '../../../components/List/Invoice/InvoiceList'
import useAxios from '../../../hooks/useAxios'
import CsvReportButton from '../../../components/Button/CsvReportButton'
import { DocumentGenerationType } from '../../../hooks/document/useDocumentGenerationHandler'
import { ModuleType } from '../../../context/GlobalContext'
import useModules from '../../../hooks/module/useModules'
import { ISearchBox, SearchBox, Stack } from '@fluentui/react'
import { useMount } from '@fluentui/react-hooks'
import { useTranslation } from 'react-i18next'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Field } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import BooleanCombobox from '../../../components/Combobox/BooleanCombobox'
import DateRange from '../../../components/DatePicker/DateRange'
import FilePicker from '../../../components/TagPicker/FilePicker'
import InvoiceStatusPicker from '../../../components/TagPicker/InvoiceStatusPicker'
import AttributesList, { parseAttributeToParams } from '../../../components/Form/Attributes/AttributesList'
import { AttributeContextType, AttributeViewType, EntityAttributeType } from '../../../hooks/useAttributes'
import useRoles, { Role } from '../../../hooks/useRoles'

const updateSettings = (settings: Partial<InvoiceListSettings>) => ({
    type: InvoiceAction.OverviewSettings,
    payload: settings,
})

export default function InvoiceOverview(): ReactElement {
    const axios = useAxios()
    const dispatch = useDispatch()
    const modules = useModules()
    const roles = useRoles()
    const { t: translate } = useTranslation()

    const settings = useSelector<Store, InvoiceListSettings>(state => state.financeInvoice.overview.settings)
    const { isViewport, viewport } = useViewport()
    const orientation = useMemo(() => (isViewport('<', Viewport.XL) ? 'vertical' : 'horizontal'), [viewport])

    const invoices = useSelector<Store, DataPromise<InvoiceOverviewDto[], InvoiceListSettings>>(
        state => state.financeInvoice.overview,
    )

    const searchBoxRef = useRef<ISearchBox>()
    const [query, setQuery] = useState<string>()

    useEffect(() => {
        if (shouldDispatchLoad(invoices)) {
            dispatch({
                type: InvoiceAction.GetOverview,
                payload: axios.get('/finance/invoice', {
                    params: { ...invoices.settings, attributes: parseAttributeToParams(invoices.settings.attributes) },
                }),
            })
        }
    }, [invoices])

    useMount(() => {
        setQuery(settings.query)

        dispatch({
            type: InvoiceAction.OverviewSettings,
            payload: {
                ...settings,
                query: settings.query ?? '',
            },
        })
    })

    useEffect(() => {
        if (searchBoxRef.current) {
            searchBoxRef.current.focus()
        }
    }, [searchBoxRef.current])

    const actionButtonItems: IActionButton[] = useMemo(
        () => [
            {
                key: 'add-invoice',
                title: translate('action.create.invoice.title'),
                secondaryText: translate('action.create.invoice.subtitle'),
                iconName: 'Add',
                route: '/finance/invoices/add',
            },
        ],
        [],
    )

    return (
        <>
            <ActionHeader actionButtonItems={actionButtonItems}>
                <DateRange
                    orientation={orientation}
                    startValue={invoices?.settings?.from}
                    endValue={invoices?.settings?.to}
                    changeStartDate={date => dispatch(updateSettings({ from: date }))}
                    changeEndDate={date => dispatch(updateSettings({ to: date }))}
                    showLabel
                />
                <Field label={translate('invoice.status.label')} orientation={orientation}>
                    <InvoiceStatusPicker
                        selectedOptions={invoices.settings?.statuses || []}
                        onOptionsSelect={(_, selectedOptions: InvoiceStatus[]) =>
                            dispatch(updateSettings({ statuses: selectedOptions }))
                        }
                    />
                </Field>
                {modules.has(ModuleType.Accounting) && (
                    <Field label={translate('invoice.filter.hasTransaction')} orientation={orientation}>
                        <BooleanCombobox
                            required
                            selectedOptions={[invoices.settings?.hasTransaction?.toString() || 'mixed']}
                            onOptionSelect={(_, data) =>
                                dispatch(
                                    updateSettings({
                                        hasTransaction: data.checked === 'mixed' ? undefined : data.checked,
                                    }),
                                )
                            }
                        />
                    </Field>
                )}
                <Field label={translate('invoice.filter.file')} orientation={orientation}>
                    <FilePicker
                        selectedOptions={invoices?.settings?.files || []}
                        onOptionsSelect={(_, selectedOptions) => dispatch(updateSettings({ files: selectedOptions }))}
                    />
                </Field>
                {modules.has(ModuleType.Attributes) && (
                    <Accordion collapsible>
                        <AccordionItem value="attributes">
                            <AccordionHeader>{translate('invoice.filter.attributes')}</AccordionHeader>
                            <AccordionPanel>
                                <AttributesList
                                    orientation={orientation}
                                    entityType={EntityAttributeType.Invoice}
                                    context={[AttributeContextType.Invoice]}
                                    viewType={AttributeViewType.Filter}
                                    entity={settings}
                                    setFieldValue={() => {
                                        dispatch(updateSettings({ attributes: settings.attributes || [] }))
                                        return Promise.resolve()
                                    }}
                                />
                            </AccordionPanel>
                        </AccordionItem>
                    </Accordion>
                )}
            </ActionHeader>
            <SearchBox
                className="clear"
                placeholder={translate('invoice.filter.search')}
                underlined
                componentRef={searchBoxRef}
                value={query ?? ''}
                onChange={(_event?: React.ChangeEvent<HTMLInputElement>, newQuery?: string) => {
                    setQuery(newQuery ?? '')
                    //number of characters reached threshold, clear settings to start searching.
                    if (query?.length < settings.minQueryLength && newQuery?.length === settings.minQueryLength) {
                        settings.prevSettings = { ...settings, prevSettings: undefined }
                    }
                    //number of characters is under threshold...
                    else if (query?.length === settings.minQueryLength && newQuery?.length < settings.minQueryLength) {
                        dispatch({
                            type: InvoiceAction.OverviewSettings,
                            payload: {
                                ...settings.prevSettings,
                                query: '',
                            },
                        })
                        return
                    }

                    if (newQuery?.length >= settings.minQueryLength) {
                        dispatch({
                            type: InvoiceAction.OverviewSettings,
                            payload: {
                                from: undefined,
                                to: undefined,
                                files: undefined,
                                statuses: undefined,
                                hasTransaction: undefined,
                                prevSettings: settings.prevSettings,

                                query: newQuery ?? '',
                                minQueryLength: settings.minQueryLength,
                                matchColumns: settings.matchColumns,
                            },
                        })
                    }
                }}
                onClear={() => {
                    setQuery('')
                    dispatch({
                        type: InvoiceAction.OverviewSettings,
                        payload: {
                            ...settings.prevSettings,
                            query: '',
                        },
                    })
                }}
            />
            {roles.all([Role.Report]) && (
                <Stack horizontal>
                    <CsvReportButton<InvoiceListSettings>
                        generationType={DocumentGenerationType.InvoiceCsv}
                        payload={() => {
                            return {
                                ...invoices?.settings,
                                attributes: undefined,
                            }
                        }}
                    />
                </Stack>
            )}
            <InvoiceList
                data={invoices.data}
                settings={invoices?.settings}
                reset={() => ({
                    type: InvoiceAction.GetOverview,
                    payload: axios.get('/finance/invoice', { params: invoices.settings }),
                })}
            />
        </>
    )
}
